import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import FirstLoginPasswordChange from "@/components/FirstLoginPasswordChange";
import { Compass, LogOut, User } from "lucide-react";

export default function StaffLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F7F5F0] text-[#1A2230]" data-testid="staff-layout">
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-[#E5DFD3]">
                <div className="max-w-6xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="h-9 w-9 rounded-full bg-[#0E2A47] grid place-items-center text-white">
                            <Compass className="h-4 w-4" strokeWidth={2.25} />
                        </span>
                        <div className="leading-tight">
                            <p className="font-[Outfit] text-[15px] font-semibold tracking-tight">
                                CRTC · Staff
                            </p>
                            <p className="text-[11px] text-[#6F6B63] uppercase tracking-[0.14em]">
                                {user?.name}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD3] hover:border-[#0E2A47] text-[13px] px-3.5 py-1.5 transition-colors"
                        data-testid="staff-logout"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-6 md:px-8 py-8">
                <Outlet />
            </main>
            {user?.must_change_password && <FirstLoginPasswordChange />}
        </div>
    );
}

/** Placeholder home for staff — filled out in Week 2 (Employee Onboarding module). */
export function StaffHome() {
    const { user } = useAuth();
    return (
        <div className="space-y-6" data-testid="staff-home">
            <div>
                <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                    Welcome
                </p>
                <h1 className="mt-2 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                    Hi {user?.name?.split(" ")[0] || "there"}.
                </h1>
                <p className="mt-3 text-[14.5px] text-[#6F6B63] max-w-2xl">
                    Your Crossroads to Change staff workspace is being built out over the
                    next few weeks. Modules shipping in the coming days:
                </p>
            </div>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { title: "Employee Handbook", body: "View, search, and acknowledge (Week 2)" },
                    { title: "Policies & Procedures", body: "Searchable, view-only library (Week 2)" },
                    { title: "Training Certificates", body: "Upload + expiry reminders (Week 2)" },
                    { title: "Client Calendar", body: "Daily / weekly schedule (Week 5)" },
                    { title: "Maintenance Requests", body: "Submit + track requests (Week 6)" },
                    { title: "AI Document Assistant", body: "Auto-complete forms (Week 7)" },
                ].map((m) => (
                    <li
                        key={m.title}
                        className="bg-white rounded-2xl border border-[#E5DFD3] p-5"
                        data-testid={`staff-tile-${m.title.replace(/\s/g, "-").toLowerCase()}`}
                    >
                        <div className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-[#EFEBE0] text-[#0E2A47] grid place-items-center">
                                <User className="h-4 w-4" />
                            </span>
                            <div>
                                <p className="font-[Outfit] font-semibold text-[15px]">{m.title}</p>
                                <p className="text-[12.5px] text-[#6F6B63]">{m.body}</p>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <p className="text-[12.5px] text-[#6F6B63]">
                Questions? Contact Valerie Payton or Dorothy Summage at 281.547.8447.
            </p>
        </div>
    );
}
